import { Layout, Model, type TabNode } from 'flexlayout-react';
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { CatalogueArticlePreview } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticlePreview';
import { CatalogueContainer } from 'app/modules/wallforming/components/catalogue-container/CatalogueContainer';
import { AutomatedPlacementContainer } from 'app/modules/wallforming/components/setting-container/automated-placement/AutomatedPlacementContainer';
import { settingOptionsModelJson } from 'app/modules/wallforming/components/setting-container/data/settingOptionsModelJson';
import { settingOptionsWithTreeModelJson } from 'app/modules/wallforming/components/setting-container/data/settingOptionsWithTreeModelJson';
import { useSettingEventHandler } from 'app/modules/wallforming/components/setting-container/hooks/useSettingEventHandler';
import { PrefabLayerTree } from 'app/modules/wallforming/components/setting-container/PrefabLayerTree';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import 'flexlayout-react/style/light.css';
import 'app/modules/wallforming/custom-styles/customFlexlayoutStyle.css';

const IS_DEV_TOOLS = import.meta.env.VITE_DEV_TOOLS === 'true';

const modelDefault = Model.fromJson(settingOptionsModelJson);
const modelWithTree = IS_DEV_TOOLS ? Model.fromJson(settingOptionsWithTreeModelJson) : modelDefault;

interface SettingOptionsLayoutProps {
    settingModelRef?: RefObject<Model | null>;
}

export const SettingOptionsLayout = memo(({ settingModelRef }: SettingOptionsLayoutProps) => {
    const catalogueViewRef = useRef<HTMLDivElement | null>(null);
    const placementViewRef = useRef<HTMLDivElement | null>(null);
    const prevSettingViewRef = useRef<SettingView>(usePlacementStore.getState().selectedSettingView);

    // Always call the hook; gate the result with the compile-time constant.
    const prefabLayerTreeOpen = usePlacementStore((state) => IS_DEV_TOOLS && state.prefabLayerTreeOpen);
    const selectedSettingView = usePlacementStore((state) => state.selectedSettingView);

    const activeModel = useMemo(() => (prefabLayerTreeOpen ? modelWithTree : modelDefault), [prefabLayerTreeOpen]);

    useSettingEventHandler({
        catalogueViewRef,
        placementViewRef,
        prevSettingViewRef,
        settingModelRef
    });

    useEffect(() => {
        if (settingModelRef) {
            settingModelRef.current = activeModel;
        }
    }, [activeModel, settingModelRef]);

    const settingOptionsFactory = useCallback((node: TabNode) => {
        const component = node.getComponent();

        if (component === 'settingOptions') {
            return (
                <div className="p-2 w-full h-full">
                    <CatalogueContainer />
                </div>
            );
        }
        if (component === 'articlePreview') {
            return (
                <div className="p-2 w-full h-full">
                    <CatalogueArticlePreview />
                </div>
            );
        }
        if (component === 'prefabLayerTree') {
            return <PrefabLayerTree />;
        }
    }, []);

    const handleCatalogueViewRef = useCallback((node: HTMLDivElement | null) => {
        catalogueViewRef.current = node;
        if (prevSettingViewRef.current === SettingView.CATALOGUE) {
            catalogueViewRef.current?.classList.remove('hidden');
        } else {
            catalogueViewRef.current?.classList.add('hidden');
        }
    }, []);

    const handlePlacementViewRef = useCallback((node: HTMLDivElement | null) => {
        placementViewRef.current = node;
        if (prevSettingViewRef.current === SettingView.AUTOMATED_PLACEMENT) {
            placementViewRef.current?.classList.remove('hidden');
        } else {
            placementViewRef.current?.classList.add('hidden');
        }
    }, []);

    return (
        <div className="settings relative flex h-full min-h-0 min-w-0 flex-1 flex-col bg-background-default m-0 p-0">
            <div ref={handleCatalogueViewRef} className="min-h-0 flex-1 overflow-auto z-0 p-2">
                <Layout
                    key={`catalogue-layout-${selectedSettingView}`}
                    factory={settingOptionsFactory}
                    model={activeModel}
                    realtimeResize={false}
                />
            </div>
            <div ref={handlePlacementViewRef} className="min-h-0 flex-1 overflow-hidden z-0 p-2">
                <AutomatedPlacementContainer />
            </div>
        </div>
    );
});

SettingOptionsLayout.displayName = 'SettingOptionsLayout';
